import FastImage from '@d11/react-native-fast-image';
import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { mvs } from 'react-native-size-matters';

import { useAppSelector } from '../../app/hooks';
import { COLORS, PODCAST_TITLE_CARD_HEIGHT } from '../../constants';
import {
  selectDefaultCover,
  selectDivRadius,
} from '../../features/appconfig/store/selectors';
import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';
import CustomText from '../CustomText';

export const PODCAST_TITLE_CARD_WIDTH = 144;

interface PodcastTitleCardProps {
  podcastThumbnail?: string;
  podcastName: string;
  onPress?: () => void;
  customWidth?: number;
}

export type { PodcastTitleCardProps };

export const PodcastTitleCard: React.FC<PodcastTitleCardProps> = ({
  podcastThumbnail,
  podcastName,
  onPress,
  customWidth,
}) => {
  const divRadius = useAppSelector(selectDivRadius);
  const podcastNameFontSize = useAdjustedFontSize(mvs(16));
  const podcastNameLineHeight = Math.round(podcastNameFontSize * 1.2);
  const defaultCover = useAppSelector(selectDefaultCover);

  const cardWidth = customWidth || mvs(PODCAST_TITLE_CARD_WIDTH);
  // thumbnail fills card width minus left+right padding
  const thumbnailSize = cardWidth - mvs(24);
  // card height = padding + thumbnail + gap + 2 title lines + padding
  const cardHeight = mvs(24) + thumbnailSize + mvs(10) + podcastNameLineHeight * 2;

  return (
    <TouchableOpacity
      style={[{ width: cardWidth, height: cardHeight }]}
      onPress={onPress}
      activeOpacity={0.95}
    >
      <View style={[styles.card, { borderRadius: mvs(divRadius) }]}>
        {/* podcast thumbnail*/}
        <View
          style={[
            styles.thumbnailContainer,
            {
              // thumbnail border radius is 70% of the main card radius for visual hierarchy
              borderRadius: mvs(divRadius) * 0.7,
              width: thumbnailSize,
              height: thumbnailSize,
            },
          ]}
        >
          <FastImage
            source={podcastThumbnail ? { uri: podcastThumbnail } : defaultCover}
            style={[styles.thumbnail]}
            resizeMode={FastImage.resizeMode.cover}
          />
        </View>

        {/* podcast name */}
        <View style={styles.titleContainer}> 
          <CustomText
            style={[
              styles.podcastName,
              { fontSize: podcastNameFontSize, lineHeight: podcastNameLineHeight },
            ]}
            weight="Regular"
            numberOfLines={2}
          >
            {podcastName}
          </CustomText>
        </View>
      </View>
    </TouchableOpacity>
  );
};

// getitemlayout function for horizontal flatlist performance
export const getPodcastTitleCardItemLayout = (_: unknown, index: number) => ({
  length: mvs(PODCAST_TITLE_CARD_WIDTH), // width of each card
  offset: mvs(PODCAST_TITLE_CARD_WIDTH + 16) * index, // width + gap between items
  index,
});

const styles = StyleSheet.create({
  card: {
    backgroundColor: COLORS.WHITE,
    padding: mvs(12),
    alignItems: 'stretch',
    gap: mvs(10),
    flex: 1,
    overflow: 'hidden',
  },
  thumbnailContainer: {
    borderRadius: mvs(13),
    overflow: 'hidden',
  },
  thumbnail: {
    width: '100%',
    height: '100%',
  },
  titleContainer: {
    justifyContent: 'flex-start',
    width: '100%',
  },
  podcastName: {
    color: 'rgba(71, 71, 71, 1)',
    textAlign: 'left',
  },
});

export default PodcastTitleCard;
